import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronLeft, ChevronRight, UserX } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { apiErrorMessage } from '@/api/client'
import { instructorPortal as api } from '@/api/endpoints'
import { Spinner } from '@/components/ui'
import { LANGUAGES, applyLanguage, preferredLanguage, setLanguage } from '@/i18n'
import { addDays, cn, getActiveLocale, toISODate } from '@/lib/utils'
import type { Attendance, InstructorPortal, InstructorPortalLesson } from '@/types/api'

/**
 * „Moje lekcie“ - mobilný pohľad inštruktora cez tajný odkaz `/i/:token`
 * (`apps/instructors/portal.py`), bez prihlásenia.
 *
 * Na svahu s mobilom v rukavici: veľké tlačidlá, jeden deň, prepínanie
 * šípkami. Po lekcii ťukne „Odučené“ alebo „Neprišiel“; druhé ťuknutie na
 * zvolené tlačidlo značku zruší.
 */
export default function InstructorPortalPage() {
  const { token = '' } = useParams()
  const { t, i18n } = useTranslation()
  // Jazyk: zvolený na tomto telefóne, inak jazyk telefónu, inak jazyk školy.
  // Určí sa pred prvou požiadavkou - aj hláška „odkaz neplatí“ je v ňom.
  const [preferred] = useState(() => {
    const lang = preferredLanguage()
    if (lang) applyLanguage(lang)
    return lang
  })
  // `?day=` z odkazu v dennom e-maile - otvorí sa deň, o ktorom e-mail je.
  const [params] = useSearchParams()
  const [day, setDay] = useState<string | undefined>(params.get('day') ?? undefined)
  const { data, isLoading, error } = useQuery({
    queryKey: ['instructor-portal', token, day],
    queryFn: () => api.get(token, day),
    refetchInterval: 60 * 1000,
  })

  useEffect(() => {
    if (!preferred && data?.lang) applyLanguage(data.lang)
  }, [preferred, data?.lang])

  const current = data?.day ?? day
  const move = (delta: number) => {
    if (!current) return
    setDay(toISODate(addDays(new Date(`${current}T00:00:00`), delta)))
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3">
          {data?.logo_url ? (
            <img src={data.logo_url} alt="" className="h-8 w-auto max-w-[96px] object-contain" />
          ) : null}
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-base font-semibold">{t('portal.title')}</div>
            <div className="truncate text-xs text-slate-500">
              {[data?.instructor, data?.school].filter(Boolean).join(' · ')}
            </div>
          </div>
          <select
            value={i18n.language}
            onChange={(e) => setLanguage(e.target.value)}
            className="rounded border border-slate-300 bg-white px-1.5 py-1 text-xs uppercase text-slate-600"
            aria-label={t('portal.language')}
          >
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
        {current ? (
          <div className="mx-auto flex max-w-xl items-center justify-between gap-2 px-2 pb-2">
            <button type="button" className="rounded-md p-3 text-slate-600 active:bg-slate-100" onClick={() => move(-1)} aria-label={t('portal.previous')}>
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="flex-1 rounded-md py-2 text-center text-sm font-medium first-letter:uppercase"
              onClick={() => setDay(data?.today)}
            >
              {formatDay(current)}
              {current !== data?.today ? (
                <span className="block text-xs font-normal text-action-700">{t('portal.backToToday')}</span>
              ) : null}
            </button>
            <button type="button" className="rounded-md p-3 text-slate-600 active:bg-slate-100" onClick={() => move(1)} aria-label={t('portal.next')}>
              <ChevronRight size={22} />
            </button>
          </div>
        ) : null}
      </header>

      <main className="mx-auto max-w-xl space-y-3 px-3 py-4">
        {isLoading ? (
          <div className="flex justify-center py-16 text-slate-400">
            <Spinner className="h-6 w-6" />
          </div>
        ) : error ? (
          <p className="rounded-lg bg-white p-6 text-center text-sm text-slate-600">
            {apiErrorMessage(error)}
          </p>
        ) : data && data.lessons.length === 0 ? (
          <p className="rounded-lg bg-white p-6 text-center text-sm text-slate-500">
            {t('portal.empty')}
          </p>
        ) : (
          data?.lessons.map((lesson) => (
            <LessonCard key={lesson.id} token={token} lesson={lesson} queryKey={['instructor-portal', token, day]} />
          ))
        )}
      </main>
    </div>
  )
}

function LessonCard({
  token,
  lesson,
  queryKey,
}: {
  token: string
  lesson: InstructorPortalLesson
  queryKey: unknown[]
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const mark = useMutation({
    mutationFn: (value: Attendance | null) => api.mark(token, lesson.id, value),
    onSuccess: ({ attendance }) => {
      setError(null)
      qc.setQueryData<InstructorPortal>(queryKey, (old) =>
        old
          ? { ...old, lessons: old.lessons.map((l) => (l.id === lesson.id ? { ...l, attendance } : l)) }
          : old,
      )
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const toggle = (value: Attendance) => mark.mutate(lesson.attendance === value ? null : value)

  return (
    <article
      className={cn(
        'rounded-lg border bg-white p-4 shadow-sm',
        lesson.attendance === 1 && 'border-emerald-300',
        lesson.attendance === 2 && 'border-rose-300',
        !lesson.attendance && 'border-transparent',
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-lg font-semibold tabular-nums">{lesson.time}</span>
        <span className="text-right text-xs font-medium uppercase text-slate-500">
          {[lesson.center, lesson.course, lesson.lang, lesson.numbers > 1 ? `${lesson.numbers} ${t('global.persons')}` : null]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>
      <div className="mt-1 text-base font-medium">{lesson.name || '—'}</div>
      {lesson.child || lesson.beginner ? (
        <div className="text-sm text-amber-700">
          {[lesson.child ? t('global.child') : null, lesson.beginner ? t('global.beginner') : null]
            .filter(Boolean)
            .join(', ')}
        </div>
      ) : null}
      {lesson.note ? (
        <p className="mt-2 whitespace-pre-line rounded bg-slate-50 p-2 text-sm text-slate-700">{lesson.note}</p>
      ) : null}

      {lesson.can_mark ? (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={mark.isPending}
            onClick={() => toggle(1)}
            className={cn(
              'flex items-center justify-center gap-2 rounded-md border py-3 text-sm font-semibold',
              lesson.attendance === 1
                ? 'border-emerald-600 bg-emerald-600 text-white'
                : 'border-emerald-300 text-emerald-700 active:bg-emerald-50',
            )}
          >
            <Check size={18} />
            {t('portal.taught')}
          </button>
          <button
            type="button"
            disabled={mark.isPending}
            onClick={() => toggle(2)}
            className={cn(
              'flex items-center justify-center gap-2 rounded-md border py-3 text-sm font-semibold',
              lesson.attendance === 2
                ? 'border-rose-600 bg-rose-600 text-white'
                : 'border-rose-300 text-rose-700 active:bg-rose-50',
            )}
          >
            <UserX size={18} />
            {t('portal.noShow')}
          </button>
        </div>
      ) : lesson.attendance ? (
        <p className={cn('mt-3 text-sm font-medium', lesson.attendance === 1 ? 'text-emerald-700' : 'text-rose-700')}>
          {lesson.attendance === 1 ? t('portal.taught') : t('portal.noShow')}
        </p>
      ) : null}
      {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
    </article>
  )
}

function formatDay(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(getActiveLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
  })
}
